import { ECAL } from './default';

const overrides = {
    components: {

        MuiCssBaseline: {
            styleOverrides: `
                /* ECAL brand CSS variables — always available globally */
                :root {
                    --ecal-navy:      ${ECAL.navy};
                    --ecal-deepblue:  ${ECAL.deepblue};
                    --ecal-midblue:   ${ECAL.midblue};
                    --ecal-lightblue: ${ECAL.lightblue};
                    --ecal-yellow:    ${ECAL.yellow};
                    --ecal-orange:    ${ECAL.orange};
                    --ecal-dorange:   ${ECAL.dorange};
                    --ecal-green:     ${ECAL.green};
                    --ecal-white:     ${ECAL.white};
                    --ecal-midgrey:   ${ECAL.midgrey};
                    --ecal-lightgrey: ${ECAL.lightgrey};
                }

                *, *::before, *::after { box-sizing: border-box; }

                html {
                    background: var(--ecal-navy);
                    color-scheme: dark;
                }

                body {
                    margin: 0;
                    background: var(--ecal-navy);
                    color: var(--ecal-white);
                    font-family: "Google Sans Flex", "Google Sans", Inter, ui-sans-serif, system-ui, Arial, sans-serif;
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                }

                /* Ambient background vignette */
                body::before {
                    content: "";
                    position: fixed; inset: 0; pointer-events: none; z-index: 0;
                    background:
                        radial-gradient(1200px 600px at 85% -10%, rgba(0, 120, 255, 0.18), transparent 60%),
                        radial-gradient(900px 500px at -5% 10%, rgba(255, 158, 0, 0.08), transparent 60%),
                        radial-gradient(1000px 700px at 50% 110%, rgba(0, 212, 86, 0.06), transparent 60%);
                }

                #root { position: relative; z-index: 1; }

                /* Gradient text utilities */
                .text-gradient-cool {
                    background: linear-gradient(90deg, ${ECAL.lightblue} 0%, ${ECAL.midblue} 100%);
                    -webkit-background-clip: text; background-clip: text; color: transparent;
                }
                .text-gradient-warm {
                    background: linear-gradient(90deg, ${ECAL.yellow} 0%, ${ECAL.dorange} 100%);
                    -webkit-background-clip: text; background-clip: text; color: transparent;
                }
                .text-gradient-full {
                    background: linear-gradient(90deg, ${ECAL.yellow} 0%, ${ECAL.orange} 25%, ${ECAL.green} 55%, ${ECAL.lightblue} 80%, ${ECAL.midblue} 100%);
                    -webkit-background-clip: text; background-clip: text; color: transparent;
                }

                /* Dynamic gradient bars */
                .bars { --bar-h: 28px; }
                @media (min-width: 768px) { .bars { --bar-h: 36px; } }
                @media (min-width: 1024px) { .bars { --bar-h: 44px; } }

                .bar {
                    display: block;
                    border-radius: 999px;
                    height: var(--bar-h);
                }
                .bar.lg { height: calc(var(--bar-h) * 2); }
                .bar.md { height: var(--bar-h); }
                .bar.sm { height: calc(var(--bar-h) * 0.5); }

                .bar-warm1   { background: linear-gradient(90deg, ${ECAL.yellow}, ${ECAL.orange}); }
                .bar-warm2   { background: linear-gradient(90deg, ${ECAL.orange}, ${ECAL.dorange}); }
                .bar-cool1   { background: linear-gradient(90deg, ${ECAL.lightblue}, ${ECAL.midblue}); }
                .bar-cool2   { background: linear-gradient(90deg, ${ECAL.green}, ${ECAL.lightblue}); }
                .bar-cool3   { background: linear-gradient(90deg, ${ECAL.midblue}, ${ECAL.deepblue}); }
                .bar-white1  { background: linear-gradient(90deg, rgba(255,255,255,0.95), ${ECAL.yellow}); }
                .bar-white2  { background: linear-gradient(90deg, rgba(255,255,255,0.95), ${ECAL.lightblue}); }
                .bar-white3  { background: linear-gradient(90deg, rgba(255,255,255,0.95), ${ECAL.midblue}); }
                .bars > .bar { mix-blend-mode: screen; }

                /* Live dot */
                @keyframes pulseGlow {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(0, 215, 255, 0.4); }
                    50%      { box-shadow: 0 0 0 12px rgba(0, 215, 255, 0); }
                }
                .live-dot {
                    display: inline-block;
                    width: 8px; height: 8px; border-radius: 999px;
                    background: linear-gradient(90deg, ${ECAL.green}, ${ECAL.lightblue});
                    animation: pulseGlow 1.6s ease-out infinite;
                }

                /* Shimmer skeleton */
                @keyframes shimmer {
                    0%   { background-position: -200% 0; }
                    100% { background-position:  200% 0; }
                }
                .shimmer {
                    background: linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.04) 100%);
                    background-size: 200% 100%;
                    animation: shimmer 1.4s linear infinite;
                }

                /* Scrollbar */
                ::-webkit-scrollbar { width: 10px; height: 10px; }
                ::-webkit-scrollbar-track { background: transparent; }
                ::-webkit-scrollbar-thumb { background: rgba(187,188,188,0.18); border-radius: 999px; }
                ::-webkit-scrollbar-thumb:hover { background: rgba(0,215,255,0.35); }

                /* Focus ring — WCAG 2.1 AA */
                *:focus-visible { outline: 2px solid ${ECAL.lightblue}; outline-offset: 2px; border-radius: 6px; }

                /* Reduce motion */
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: 0.001ms !important;
                        transition-duration: 0.001ms !important;
                    }
                }
            `,
        },

        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    padding: '0.65rem 1.15rem',
                    fontWeight: 600,
                    letterSpacing: '0.01em',
                    transition: 'transform 0.18s ease, box-shadow 0.25s ease, filter 0.25s ease',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                    },
                    '&:focus-visible': {
                        outline: `2px solid ${ECAL.lightblue}`,
                        outlineOffset: 3,
                    },
                },
                contained: {
                    background: `linear-gradient(90deg, ${ECAL.midblue} 0%, ${ECAL.lightblue} 100%)`,
                    color: ECAL.white,
                    boxShadow: '0 12px 28px -10px rgba(0, 120, 255, 0.55)',
                    '&:hover': {
                        filter: 'saturate(1.1)',
                        boxShadow: '0 16px 32px -12px rgba(0, 215, 255, 0.6)',
                    },
                },
                outlined: {
                    background: ECAL.navy,
                    color: ECAL.white,
                    border: 'none',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        padding: '1.5px',
                        borderRadius: 999,
                        background: `linear-gradient(90deg, ${ECAL.lightblue}, ${ECAL.midblue})`,
                        WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                        pointerEvents: 'none',
                    },
                },
                sizeLarge: {
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                },
                sizeSmall: {
                    padding: '0.35rem 0.85rem',
                    fontSize: '0.8125rem',
                },
            },
        },

        MuiContainer: {
            defaultProps: {
                maxWidth: false,
            },
            styleOverrides: {
                root: {
                    maxWidth: '1280px',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    paddingLeft: '32px !important',
                    paddingRight: '32px !important',
                    '@media (min-width: 600px)': {
                        paddingLeft: '32px !important',
                        paddingRight: '32px !important',
                    },
                    '@media (min-width: 900px)': {
                        paddingLeft: '32px !important',
                        paddingRight: '32px !important',
                    },
                },
                // Prevent MuiContainer-maxWidthXl from expanding to 1536px at ≥1536px
                maxWidthXl: {
                    maxWidth: '1280px !important',
                    '@media (min-width: 1536px)': {
                        maxWidth: '1280px !important',
                    },
                },
            },
        },

        MuiCard: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: {
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.015) 100%)',
                    border: '1px solid rgba(187, 188, 188, 0.14)',
                    borderRadius: 22,
                    padding: '1.25rem',
                    transition: 'transform 0.25s ease, border-color 0.25s ease, box-shadow 0.35s ease',
                    '&:hover': {
                        transform: 'translateY(-3px)',
                        borderColor: 'rgba(0, 215, 255, 0.35)',
                        boxShadow: '0 24px 48px -22px rgba(0, 215, 255, 0.35)',
                    },
                },
            },
        },

        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(187,188,188,0.18)',
                    color: ECAL.lightgrey,
                    fontSize: '0.78rem',
                    fontWeight: 500,
                    transition: 'all 0.18s ease',
                    '&:hover': {
                        color: ECAL.white,
                        borderColor: 'rgba(0,215,255,0.6)',
                        background: 'rgba(0,120,255,0.12)',
                    },
                },
                colorPrimary: {
                    color: '#001025',
                    background: `linear-gradient(90deg, ${ECAL.lightblue}, ${ECAL.midblue})`,
                    borderColor: 'transparent',
                },
            },
        },

        MuiTextField: {
            defaultProps: { variant: 'outlined', size: 'small' },
        },

        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    background: 'rgba(0,0,0,0.30)',
                    color: ECAL.white,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.10)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.25)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: ECAL.lightblue,
                    },
                    '& input::placeholder': {
                        color: ECAL.midgrey,
                        opacity: 1,
                    },
                },
            },
        },

        MuiDialog: {
            styleOverrides: {
                paper: {
                    background: '#060638',
                    border: '1px solid rgba(187,188,188,0.18)',
                    borderRadius: 24,
                    boxShadow: '0 48px 80px -32px rgba(0,0,0,0.8), 0 0 0 1px rgba(0,120,255,0.18)',
                },
                backdrop: {
                    background: 'rgba(1,1,52,0.72)',
                    backdropFilter: 'blur(8px)',
                },
            },
        },

        MuiAppBar: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: {
                    background: 'transparent',
                    borderBottom: '1px solid transparent',
                    transition: 'backdrop-filter 0.25s ease, background 0.25s ease, border-color 0.25s ease',
                    '&.scrolled': {
                        background: 'rgba(1,1,52,0.78)',
                        backdropFilter: 'saturate(160%) blur(14px)',
                        borderBottomColor: 'rgba(187,188,188,0.12)',
                    },
                },
            },
        },

        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: 'rgba(187,188,188,0.12)',
                },
            },
        },

        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    background: '#060638',
                    border: '1px solid rgba(187,188,188,0.18)',
                    borderRadius: 8,
                    fontSize: '0.78rem',
                    color: ECAL.white,
                },
            },
        },

        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.05)',
                    height: 8,
                },
                bar: {
                    borderRadius: 999,
                    background: `linear-gradient(90deg, ${ECAL.green}, ${ECAL.lightblue}, ${ECAL.midblue})`,
                },
            },
        },
    },
};

export default overrides;
